import * as React from 'react'
import { forwardRef, Ref, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { useService } from '@pushly/aqe/lib/hooks'
import { AppService, DomainIntegrationService, JourneyService } from '../../services'
import {
    Background,
    BackgroundVariant,
    Controls,
    MiniMap,
    ReactFlow,
    ReactFlowProps,
    ReactFlowProvider,
} from '@xyflow/react'
import { LoadingOutlined } from '@ant-design/icons'
import { Drawer, Well } from '@pushly/aqe/lib/components'
import { CustomNode } from './node-types/custom-node/custom-node'
import { CustomEdge } from './edge-types/custom-edge/custom-edge'
import { SplitStepNode } from './node-types/split-step-node/split-step-node'
import { PerfCardNode } from './node-types/perf-card-node/perf-card-node'
import { ConditionStepNode } from './node-types/condition-step-node/condition-step-node'
import { BranchEdge } from './edge-types/branch-edge/branch-edge'
import { NodeEditor } from './editors/node-editor'
import { JourneyBuilderMode } from './enums'
import { StepType, JourneyActionType } from '@pushly/models/lib/enums/journeys'
import { DomainDto } from '../../dtos/domain'
import { JourneyContext } from './context'
import { useJourneyReducer } from './reducer'
import titleCase from 'title-case'
import { getClassNames } from '../../_utils/classnames'
import { buildInitialJourneyFlow, parseJourneyFromFlow, graphHasMeaningfulSteps } from './helpers'
import { buildDefaultSteps } from '@pushly/models/lib/structs/journeys'
import { getRandId } from '@pushly/models/lib/utils/get-rand-id'
import { validateSplitStepGraph } from './utils/graph/validation'
import { simpleNotification } from '../../_utils/utils'
import { getEditableState } from '../../components/journey-builder/helpers/journey'
import { JourneyEditableState } from '@pushly/models/lib/enums/journeys'
import { Journey } from './types/journey-api-response'
import {
    JourneyEdge,
    JourneyNode,
    NodeEditorRef,
    isActionJourneyNode,
    isActionStep,
    isVariantSplitStep,
    SendNotificationActionParams,
} from './types/journey-nodes'
import { CopyStates, JourneyAction } from './types/journey-context'
import { GetDomainIntegrationResponse } from '@pushly/models/lib/structs/domains'
import { getStepConfig, resolveLabel } from './step-config'
import { getJourneyDisplayItemType } from '../../_utils/journeys'
import './styles/journey.scss'
import { RemoveStepConfirmationModal } from './modals/remove-step-confirmation-modal'
import { PromoteVariantModal } from './modals/promote-variant-modal'
import { JourneyStatusRef } from './types/journey-nodes'
import { Prompt, useLocation } from 'react-router-dom'
import { ViewportAdjuster, ViewportAdjusterRef } from './viewport-adjuster'
import { useRenderedJourneyGraph } from './layout/use-rendered-journey-graph'
import { useThemeMode } from '@pushly/aqe'
import { SplitTestResultsPanel } from './split-test-results/split-test-results-panel'
import {
    SplitTestResultsContext,
    SplitTestResultsContextValue,
} from './split-test-results/split-test-results-context'
import { useVariantMetrics } from './split-test-results/use-variant-metrics'
import { computeLeaderCuesBySplit, SplitBranchRef } from './split-test-results/split-test-metrics'
import { SubjectEntity } from '../../enums/ability-entity.enum'
import { AbilityAction } from '../../enums/ability-action.enum'
import { StatusType } from '../../enums/status-type'
import { Container } from 'typescript-ioc/es5'
import { AppState } from '../../stores/app'

const MINIMAP_ZOOM_THRESHOLD = 0.85

type JourneyBuilderProps = {
    ref?: Ref<JourneyStatusRef>
    loading?: boolean
    domain: DomainDto
    journey: Journey
    domainIntegrations?: GetDomainIntegrationResponse[]
    onChange?: (journey: Journey) => void
    onSubmit?: (journey: Journey, remove?: boolean) => void
    mode?: JourneyBuilderMode
    flowOverrides?: ReactFlowProps<JourneyNode, JourneyEdge>
    /**
     * Optional ReactNode rendered in the Well header row, floated right alongside
     * the "Builder" title. Used to inject the RevisionSelector.
     */
    headerActions?: React.ReactNode
    /**
     * Optional ReactNode rendered at the top of the Well body, directly under the
     * header bar. Used to inject the read-only amber callout.
     */
    bodyHeader?: React.ReactNode
    /**
     * When true, promotion is not an available action (e.g. viewing a past
     * revision). The Split Test Results panel hides its Promote buttons.
     * Distinct from READONLY mode, which the Workflow tab uses for the active
     * revision too — only a PAST revision sets this.
     */
    promotionDisabled?: boolean
}

export const JourneysBuilder = forwardRef<JourneyStatusRef, JourneyBuilderProps>(
    ({ loading, domain, journey, domainIntegrations, mode, onChange, onSubmit, flowOverrides, headerActions, bodyHeader, promotionDisabled = false }, ref) => {
        // region Builder Definitions
        const appSvc = useService(AppService)
        const domainIntegrationSvc = useService(DomainIntegrationService)

        const loc = useLocation()
        const locSearchParams = new URLSearchParams(loc.search)

        const [loadingDependencies, setLoadingDependencies] = React.useState(false)
        const [removeModalOpen, setRemoveModalOpen] = React.useState(false)
        const [splitTestResultsPanelOpen, setSplitTestResultsPanelOpen] = React.useState(false)
        // Which split test the Results panel is scoped to (graph node id of the
        // VARIANT_SPLIT the user opened it from). A journey can hold several
        // independent split tests; the panel only ever shows one.
        const [activeSplitId, setActiveSplitId] = React.useState<string | null>(null)

        // Promote variant modal state (declared here; handlers below after state/dispatch)
        const journeyService = useService(JourneyService)
        const [promoteModalState, setPromoteModalState] = React.useState<{
            open: boolean
            variantId: string | number | null
            variantName: string
            stepId: number | string | null
            branchKey: string
            otherVariantCount: number
        }>({
            open: false,
            variantId: null,
            variantName: '',
            stepId: null,
            branchKey: '',
            otherVariantCount: 0,
        })

        /**
         * Check if the current user can edit journeys.
         * Journeys are dual-mounted under /campaigns in the API; the ability
         * entity follows the same pattern as journey-table-well.tsx.
         */
        const canPromoteVariant = React.useMemo(() => {
            const appState = Container.get(AppState)
            return appState.abilityStore.can(
                AbilityAction.UPDATE,
                appState.abilityStore.getDomainOwnedIdentityFor(SubjectEntity.CAMPAIGN),
            )
        }, [])

        const [zoomLevel, setZoomLevel] = React.useState(1)

        const nodeRef = useRef<NodeEditorRef>(null)
        const builderRef = useRef<{ builderHasChanged: boolean }>({ builderHasChanged: false })
        const viewportAdjusterRef = useRef<ViewportAdjusterRef>(null)

        // Nodes are rendered from these types passed to ReactFlow StepTypes prop
        // node.type === StepTypes[node.type] determines render component
        const StepTypes = useMemo(
            () => ({
                custom: CustomNode,
                'split-step': SplitStepNode,
                'condition-step': ConditionStepNode,
                'perf-card': PerfCardNode,
            }),
            [],
        )

        // Edges are rendered the these types passed to ReactFlow.edgeTypes
        // edge.type === edgeTypes[node.type] determines render component
        const edgeTypes = useMemo(
            () => ({
                custom: CustomEdge,
                'branch-edge': BranchEdge,
            }),
            [],
        )
        // endregion

        // region Node Handlers
        // --------------- Node Interactions -------------------- //

        // local handlers

        const handleCloseEditor = () => {
            dispatch({ action: JourneyAction.SET_ACTIVE_NODE, data: null })
        }

        const handlePaneClick = useCallback(() => {
            dispatch({ action: JourneyAction.CLOSE_ALL_POPOVERS })
        }, [])

        const handleMove = useCallback(() => {
            dispatch({ action: JourneyAction.CLOSE_ALL_POPOVERS })
        }, [])

        const handleViewportChange = useCallback((viewport: { zoom: number }) => {
            setZoomLevel(viewport.zoom)
        }, [])

        const handleSubmitJourney = () => {
            // Run split step graph validation before save
            const graphValidation = validateSplitStepGraph(state.nodes, state.edges)
            if (!graphValidation.valid) {
                const firstError = graphValidation.errors[0]
                simpleNotification('error', firstError?.message ?? 'Journey contains validation errors')
                return
            }

            const journeyUpdate = parseJourneyFromFlow(state)
            if (!journeyUpdate) return

            const persistedStepIds = new Set((journey.revision?.steps ?? []).map((step) => String(step.id)))
            const hasRemovedPersistedSteps = state.removedNodes.some((node) => {
                const removedStepId = node.data?.step?.id ?? node.id
                return removedStepId != null && persistedStepIds.has(String(removedStepId))
            })

            if (
                mode !== JourneyBuilderMode.CREATE &&
                // @todo NS-1663 - update to Journey types/helpers
                getEditableState(journey) === JourneyEditableState.RUNNING &&
                hasRemovedPersistedSteps
            ) {
                setRemoveModalOpen(true)
            } else {
                onSubmit?.(journeyUpdate)
            }
        }

        // endregion

        // initialize journey context
        //
        // The journey from the API is already a plain object (no struct instances).
        // structuredClone produces a deep copy for the reducer's initial state.
        const plainJourney: Journey = structuredClone(journey)
        const [state, dispatch] = useJourneyReducer({
            domain,
            journey: plainJourney,
            mode: mode ?? JourneyBuilderMode.READONLY,
            nodes: [],
            edges: [],
            copy: { state: CopyStates.CLEAR, node: null },
            editorOpen: false,
        })

        const { renderNodes, renderEdges, onRenderNodesChange, onRenderEdgesChange, clearRenderGraph } =
            useRenderedJourneyGraph({
                canonicalNodes: state.nodes,
                canonicalEdges: state.edges,
            })

        /**
         * Called by SplitTestResultsPanel when the user clicks Promote.
         * Looks up the variant's name + stepId + branchKey from the active revision,
         * then opens the confirmation modal.
         */
        const handlePromoteClick = React.useCallback(
            (variantKey: string | number) => {
                if (!canPromoteVariant) return

                const currentJourney = state.journey
                if (!currentJourney) return

                // Locate the branch by its key across EVERY VARIANT_SPLIT step in the
                // active revision. branch.key is a globally-unique nanoid, so a
                // journey with multiple independent splits resolves to the correct one —
                // the old "first split step only" lookup silently no-op'd for any variant
                // that lived in a later split. (Callers must pass branch.key, not the
                // warehouse variant.id, which collapses to 0 for every row.)
                const splitSteps = (currentJourney.revision?.steps ?? []).filter(isVariantSplitStep)

                let foundStep: (typeof splitSteps)[number] | undefined
                let foundBranch: { key: string; label: string } | undefined
                for (const step of splitSteps) {
                    const branches = step.configuration.branches ?? []
                    const branch = branches.find((b) => String(b.key) === String(variantKey))
                    if (branch) {
                        foundStep = step
                        foundBranch = branch
                        break
                    }
                }

                if (!foundStep || !foundBranch) return

                const otherVariantCount = Math.max(
                    0,
                    (foundStep.configuration.branches ?? []).length - 1,
                )

                setPromoteModalState({
                    open: true,
                    variantId: variantKey,
                    variantName: foundBranch.label,
                    stepId: foundStep.id,
                    branchKey: foundBranch.key,
                    otherVariantCount,
                })
            },
            [state.journey, canPromoteVariant],
        )

        /**
         * Called when the user confirms promotion in the modal.
         * Calls the API, then refetches the journey to reflect the now-linear revision.
         */
        const handlePromoteConfirm = React.useCallback(async () => {
            const { stepId, branchKey } = promoteModalState
            const currentJourney = state.journey
            if (!currentJourney || stepId == null || !branchKey) return

            const result = await journeyService.promoteVariant(currentJourney.id, stepId, branchKey)

            if (result.ok) {
                // Close the panel + modal, then refetch the now-collapsed journey.
                setPromoteModalState((prev) => ({ ...prev, open: false }))
                setSplitTestResultsPanelOpen(false)

                const refreshed = await journeyService.fetchById(currentJourney.id)
                if (refreshed.ok && refreshed.data) {
                    dispatch({ action: JourneyAction.SET_JOURNEY, data: refreshed.data })

                    // Promotion mints a NEW revision under the SAME journey id, so the
                    // mount-time build effect (keyed on journey.id) does NOT re-run.
                    // Rebuild the canvas from the new collapsed revision so it reflects
                    // the promotion immediately — without this the canvas keeps showing
                    // the pre-promotion split until a full page refresh. Guard `.revision`
                    // so a malformed refetch degrades gracefully (journey state still
                    // updated above) instead of throwing in the click handler.
                    const collapsedSteps = refreshed.data.revision?.steps
                    if (collapsedSteps) {
                        const [nodes, edges] = buildInitialJourneyFlow(collapsedSteps, {
                            integrations: state.dependencies?.integrations ?? new Map(),
                        })
                        clearRenderGraph()
                        dispatch({ action: JourneyAction.SET_ACTIVE_NODE, data: null })
                        dispatch({ action: JourneyAction.ON_JOURNEY_CHANGES, data: { nodes, edges } })

                        // The promotion is persisted server-side — it is NOT an unsaved
                        // local edit. Re-baseline the dirty snapshot for the collapsed
                        // graph. Reset the enrichment GATE (snapshotReady=false) rather than
                        // nulling-and-relying-on-the-50ms-timer: the new action nodes fire
                        // async notification enrichment, and capturing the baseline before it
                        // completes would compare enriched nodes against a pre-enrichment
                        // baseline → spurious "unsaved changes" / "Leave site?" right after a
                        // successful promote. Mirrors the mount path (capture after enrichment).
                        initialSnapshotRef.current = null
                        pendingEnrichmentsRef.current = 0
                        setSnapshotReady(false)
                        builderRef.current.builderHasChanged = false
                    }
                }
            }
            // Error handling is done inside promoteVariant via handleResponseErrorMessage
            // which shows an antd notification. For 409 the fallback message is shown.
        }, [promoteModalState, state.journey, state.dependencies, journeyService, dispatch, clearRenderGraph])

        // Shared split-test variant metrics: fetch ONCE here and share via
        // SplitTestResultsContext. Both the per-variant fork chips (rendered in branch
        // edges) and the Results panel consume this — no duplicate fetches, and a single
        // leader cue so the canvas chips and the panel always agree.
        // CRITICAL: rows come exclusively from the API (post-promotion the active
        // graph is linear and has no branches to walk).
        const splitTestIsDraft = state.journey?.status === StatusType.DRAFT.name
        const splitTestMetrics = useVariantMetrics(state.journey, !splitTestIsDraft, 'canvas')
        const splitTestRows =
            splitTestMetrics.status === 'success' ? splitTestMetrics.rows : []
        // Every VARIANT_SPLIT node's step id — the reliable key for scoping
        // variant metrics rows to one split test (rows carry split_test.step_id).
        const splitTestStepIds = React.useMemo(
            () => state.nodes.filter((n) => isVariantSplitStep(n.data.step)).map((n) => n.id),
            [state.nodes],
        )
        const splitTestLeaderCueBySplitId = React.useMemo(
            () => computeLeaderCuesBySplit(splitTestStepIds, splitTestRows),
            [splitTestStepIds, splitTestRows],
        )
        // The split the Results panel is scoped to: its step id (for row scoping)
        // + its branches in canvas/lane order (so the drawer lists variants in the
        // same order as the canvas — no sort control needed).
        const activeSplit = React.useMemo<{ splitId: string; branches: SplitBranchRef[] } | null>(() => {
            if (!activeSplitId) return null
            const node = state.nodes.find((n) => n.id === activeSplitId)
            const step = node?.data.step
            if (!node || !step || !isVariantSplitStep(step)) return null
            return {
                splitId: activeSplitId,
                branches: (step.configuration.branches ?? []).map((b) => ({ key: b.key, label: b.label })),
            }
        }, [activeSplitId, state.nodes])
        const splitTestIsLoading =
            !splitTestIsDraft &&
            (splitTestMetrics.status === 'loading' || splitTestMetrics.status === 'idle')

        const splitTestResultsContextValue: SplitTestResultsContextValue = React.useMemo(
            () => ({
                openResultsPanel: (splitId: string) => {
                    setActiveSplitId(splitId)
                    setSplitTestResultsPanelOpen(true)
                },
                onPromote: handlePromoteClick,
                metrics: splitTestMetrics,
                rows: splitTestRows,
                leaderCueBySplitId: splitTestLeaderCueBySplitId,
                isLoading: splitTestIsLoading,
                isDraft: splitTestIsDraft,
            }),
            [
                handlePromoteClick,
                splitTestMetrics,
                splitTestRows,
                splitTestLeaderCueBySplitId,
                splitTestIsLoading,
                splitTestIsDraft,
            ],
        )

        // Emit onChange from canonical reducer state.
        // Guard against initial empty state to avoid spurious emissions on mount.
        useEffect(() => {
            if (state.nodes.length > 0) {
                const updated = parseJourneyFromFlow(state)
                if (updated) onChange?.(updated)
            }
        }, [state.nodes, state.edges])

        // on mount build journey flow, dispatch to journey context initial states
        useEffect(() => {
            const fetchDomainIntegrations = async (): Promise<Map<number, GetDomainIntegrationResponse>> => {
                setLoadingDependencies(true)
                const response = await domainIntegrationSvc.fetchAll(domain.id)
                setLoadingDependencies(false)

                if (response.ok && response.data?.length) {
                    return new Map(response.data.map((int) => [int.id, GetDomainIntegrationResponse.from(int)]))
                }

                return new Map()
            }

            const buildInitialJourneyNodesAndEdges = async () => {
                clearRenderGraph()

                const integrations = domainIntegrations?.length
                    ? new Map(domainIntegrations.map((int) => [int.id, GetDomainIntegrationResponse.from(int)]))
                    : await fetchDomainIntegrations()

                dispatch({
                    action: JourneyAction.SET_JOURNEY_INTEGRATIONS,
                    data: integrations,
                })

                if (journey) {
                    const steps =
                        mode === JourneyBuilderMode.CREATE
                            ? // buildDefaultSteps returns class-based JourneyStep[] while Journey['steps']
                              // expects interface-based IJourneyStep[]. The double cast bridges this mismatch.
                              (buildDefaultSteps(getRandId) as unknown as Journey['steps'])
                            : journey.revision.steps

                    const [initialNodes, initialEdges] = buildInitialJourneyFlow(steps, {
                        integrations,
                    })

                    dispatch({ action: JourneyAction.SET_JOURNEY, data: journey })
                    dispatch({
                        action: JourneyAction.ON_JOURNEY_CHANGES,
                        data: { nodes: initialNodes, edges: initialEdges },
                    })

                    const initialNodeId = locSearchParams.get('step_id')
                    if (initialNodeId && initialNodeId.match(/^\d+$/)) {
                        const initialActiveNode = initialNodes.find((n) => String(n.id) === initialNodeId) ?? null
                        if (initialActiveNode) {
                            dispatch({ action: JourneyAction.SET_ACTIVE_NODE, data: initialActiveNode })
                        }
                    }
                }
            }

            buildInitialJourneyNodesAndEdges()
        }, [journey?.id])
        // endregion

        // region Snapshot-based dirty detection
        // Instead of per-node hash tracking (UIEditTrackable.hasChanged), we capture a
        // canonical snapshot of the step data once the flow is fully initialized (including
        // async notification enrichment in CustomNode). We compare against that snapshot
        // to determine if the user has made meaningful changes.

        const initialSnapshotRef = useRef<string | null>(null)
        const [snapshotReady, setSnapshotReady] = useState(false)
        const pendingEnrichmentsRef = useRef(0)

        // Called by CustomNode when it starts/finishes async notification fetching.
        // Once all enrichments complete, we capture the initial snapshot.
        const enrichmentCallbacks = useMemo(
            () => ({
                onEnrichStart: () => {
                    pendingEnrichmentsRef.current++
                },
                onEnrichEnd: () => {
                    pendingEnrichmentsRef.current = Math.max(0, pendingEnrichmentsRef.current - 1)
                    if (pendingEnrichmentsRef.current === 0 && !snapshotReady) {
                        setSnapshotReady(true)
                    }
                },
            }),
            [snapshotReady],
        )

        // Normalize steps for comparison: strip UI-only fields, sort keys
        const normalizeStepsForSnapshot = useCallback((nodes: JourneyNode[]): string => {
            const steps = nodes
                .filter((n) => n.data.step.type !== StepType.GLOBAL_TRIGGER)
                .map((n) => {
                    const { step } = n.data
                    // Only compare step-level data that matters for persistence:
                    // id, type, configuration (without UI enrichments like notification object for display)
                    if (isActionStep(step)) {
                        const params = step.configuration.params as SendNotificationActionParams
                        // Strip the full notification object (enrichment/display only)
                        // but include a template fingerprint so content edits are detected
                        const { notification, ...restParams } = params
                        const contentFingerprint = notification?.template
                            ? JSON.stringify(notification.template)
                            : undefined
                        return {
                            id: step.id,
                            type: step.type,
                            configuration: {
                                ...step.configuration,
                                params: { ...restParams, _tf: contentFingerprint },
                            },
                        }
                    }
                    return { id: step.id, type: step.type, configuration: step.configuration }
                })
                .sort((a, b) => String(a.id).localeCompare(String(b.id)))
            return JSON.stringify(steps)
        }, [])

        // Capture initial snapshot once all async enrichments are done
        useEffect(() => {
            if (!snapshotReady || initialSnapshotRef.current !== null) return
            // Small delay to ensure the final reducer state has settled
            const timer = setTimeout(() => {
                if (state.nodes.length > 0) {
                    initialSnapshotRef.current = normalizeStepsForSnapshot(state.nodes)
                }
            }, 50)
            return () => clearTimeout(timer)
        }, [snapshotReady, state.nodes])

        // If there are no action nodes that need enrichment, mark snapshot ready immediately
        useEffect(() => {
            if (state.nodes.length > 0 && pendingEnrichmentsRef.current === 0 && !snapshotReady) {
                // Check if any action nodes exist that would trigger enrichment
                const hasActionNodes = state.nodes.some(
                    (n) => isActionJourneyNode(n) && n.data.step.configuration?.params?.notification_id,
                )
                if (!hasActionNodes) {
                    setSnapshotReady(true)
                }
            }
        }, [state.nodes.length, snapshotReady])

        const detectChanges = useCallback(() => {
            // No snapshot yet — not dirty (avoids false positive on load)
            if (initialSnapshotRef.current === null) return false

            // In CREATE mode, a scaffolding-only graph is not meaningfully modified
            if (mode === JourneyBuilderMode.CREATE && !graphHasMeaningfulSteps(state.nodes)) {
                return false
            }

            const currentSnapshot = normalizeStepsForSnapshot(state.nodes)
            return currentSnapshot !== initialSnapshotRef.current
        }, [state.nodes, normalizeStepsForSnapshot, mode])

        // endregion

        useEffect(() => {
            if (state.journey?.id) {
                /** Track if builder has changed for submit button text and beforeunload warning */
                const changed = detectChanges()
                builderRef.current.builderHasChanged = changed
            }
        }, [state.nodes, JSON.stringify(state.journey), detectChanges])

        useImperativeHandle(ref, () => ({
            builderHasChanged: builderRef.current.builderHasChanged ?? false,
            markSaveComplete: () => {
                builderRef.current.builderHasChanged = false
                // Reset snapshot to current state so future comparisons start clean
                initialSnapshotRef.current = normalizeStepsForSnapshot(state.nodes)
            },
        }))

        // Warn user before closing/navigating away with unsaved changes
        useEffect(() => {
            if (mode === JourneyBuilderMode.READONLY) return

            const handler = (e: BeforeUnloadEvent) => {
                if (builderRef.current.builderHasChanged) {
                    e.preventDefault()
                }
            }

            window.addEventListener('beforeunload', handler)
            return () => window.removeEventListener('beforeunload', handler)
        }, [mode])

        const { resolvedMode } = useThemeMode()

        const drawerWidth = useMemo(() => {
            const step = state.activeNode?.data?.step
            if (!step) return 1000

            switch (step.type) {
                case StepType.DELAY:
                    return 600
                case StepType.VARIANT_SPLIT:
                    return 480
                case StepType.ACTION: {
                    const actionType = step.configuration?.type
                    if (actionType === JourneyActionType.SEND_RECOMMENDED_NOTIFICATION) {
                        return 1015
                    }
                    return '75%'
                }
                default:
                    return 1000
            }
        }, [state.activeNode])

        return (
            <Well
                className={getClassNames('journey-builder-well', 'nested')}
                title="Builder"
                actions={headerActions}
                onSubmit={handleSubmitJourney}
                submitText={builderRef.current?.builderHasChanged ? 'Save Changes' : 'Save'}
                hideSubmit={mode === JourneyBuilderMode.READONLY}
                hideCancel={true}
                loading={false}
                bodyStyle={{ padding: 0 }}
                footerMode="ghost"
            >
                <Prompt
                    when={mode !== JourneyBuilderMode.READONLY}
                    message={() => {
                        if (builderRef.current?.builderHasChanged) {
                            return 'You have unsaved changes. Are you sure you want to leave?'
                        }
                        return true
                    }}
                />
                {/* amber read-only callout + "Back to current revision" button */}
                {bodyHeader}
                {loading || loadingDependencies ? (
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%',
                            minHeight: 400,
                        }}
                    >
                        <LoadingOutlined spin style={{ fontSize: 32, color: 'var(--color-gray-dark)' }} />
                    </div>
                ) : (
                    state.journey && (
                        <JourneyContext.Provider value={{ state, dispatch, enrichment: enrichmentCallbacks }}>
                            <SplitTestResultsContext.Provider value={splitTestResultsContextValue}>
                            <ReactFlowProvider>
                                <ViewportAdjuster ref={viewportAdjusterRef} nodes={renderNodes} edges={renderEdges} />
                                <ReactFlow<JourneyNode, JourneyEdge>
                                    className={getClassNames('journey-builder')}
                                    nodes={renderNodes}
                                    edges={renderEdges}
                                    nodeTypes={StepTypes}
                                    onNodesChange={onRenderNodesChange}
                                    nodesConnectable={false}
                                    nodesDraggable={false}
                                    nodesFocusable={mode !== JourneyBuilderMode.READONLY}
                                    edgeTypes={edgeTypes}
                                    onEdgesChange={onRenderEdgesChange}
                                    edgesReconnectable={false}
                                    edgesFocusable={false}
                                    onPaneClick={handlePaneClick}
                                    onMove={handleMove}
                                    onViewportChange={handleViewportChange}
                                    {...flowOverrides}
                                    selectionOnDrag={false}
                                    selectionKeyCode={null}
                                    maxZoom={1}
                                    zoomOnDoubleClick={false}
                                    proOptions={{
                                        hideAttribution: true,
                                    }}
                                    colorMode={resolvedMode}
                                >
                                    <Background
                                        variant={BackgroundVariant.Lines}
                                        bgColor="var(--ant-color-bg-layout)"
                                        color="var(--color-gray-light)"
                                        lineWidth={0.5}
                                        gap={15}
                                    />
                                    <Controls
                                        position="top-right"
                                        orientation="horizontal"
                                        showInteractive={false}
                                        showFitView={false}
                                    >
                                        <button
                                            type="button"
                                            className="react-flow__controls-button react-flow__controls-fitview"
                                            title="Fit View"
                                            aria-label="Fit View"
                                            onClick={() => viewportAdjusterRef.current?.fitView()}
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 30">
                                                <path d="M3.692 4.63c0-.53.4-.938.939-.938h5.215V0H4.708C2.13 0 0 2.054 0 4.63v5.216h3.692V4.631zM27.354 0h-5.2v3.692h5.17c.53 0 .984.4.984.939v5.215H32V4.631A4.624 4.624 0 0027.354 0zm.954 24.83c0 .532-.4.94-.939.94h-5.215v3.768h5.215c2.577 0 4.631-2.13 4.631-4.707v-5.139h-3.692v5.139zm-23.677.94c-.531 0-.939-.4-.939-.94v-5.138H0v5.139c0 2.577 2.13 4.707 4.708 4.707h5.138V25.77H4.631z" />
                                            </svg>
                                        </button>
                                    </Controls>
                                    {zoomLevel < MINIMAP_ZOOM_THRESHOLD && (
                                        <MiniMap
                                            position="bottom-right"
                                            pannable
                                            zoomable={false}
                                            nodeStrokeWidth={3}
                                            style={{ opacity: Math.min(1, (MINIMAP_ZOOM_THRESHOLD - zoomLevel) / 0.2) }}
                                        />
                                    )}
                                </ReactFlow>
                            </ReactFlowProvider>

                            <Drawer
                                getContainer={appSvc.getAppContainer}
                                className={getClassNames(
                                    'journey-node-editor',
                                    state.activeNode?.data?.step?.type,
                                    state.activeNode?.data?.step?.configuration?.type,
                                )}
                                destroyOnHidden={true}
                                title={`${titleCase(
                                    state.mode === JourneyBuilderMode.READONLY ? 'View' : state.mode,
                                )} ${(() => {
                                    const node = state.activeNode
                                    if (!node) return ''
                                    const config = getStepConfig(node.data.step)
                                    if (!config || config.kind !== 'user') return ''
                                    return resolveLabel(
                                        config,
                                        getJourneyDisplayItemType(state.journey, state.domain.ecommConfig?.itemType),
                                    )
                                })()}`}
                                placement="right"
                                closable={true}
                                mask={{ closable: false }}
                                open={state.editorOpen}
                                onClose={handleCloseEditor}
                                hideFooter={mode === JourneyBuilderMode.READONLY}
                                submitText="Update Step"
                                onSubmit={async () => {
                                    const result = await nodeRef?.current?.onSubmit()

                                    if (result) {
                                        dispatch({ action: JourneyAction.ON_NODE_STEP_CHANGE, data: result })
                                        if (mode !== JourneyBuilderMode.READONLY) {
                                            simpleNotification(
                                                'warning',
                                                "Step updated. Don't forget to save your journey.",
                                            )
                                        }
                                    }

                                    // Always close the drawer, even if no changes were made
                                    dispatch({ action: JourneyAction.SET_ACTIVE_NODE, data: null })
                                }}
                                footer={state.editorFooter}
                                size={drawerWidth}
                            >
                                {state.activeNode && <NodeEditor node={state.activeNode!} ref={nodeRef} />}
                            </Drawer>

                            <RemoveStepConfirmationModal
                                changeSet={{ campaign: parseJourneyFromFlow(state) }}
                                visible={removeModalOpen}
                                onConfirm={(updatedJourney, removeSubs) => {
                                    setRemoveModalOpen(false)
                                    onSubmit?.(updatedJourney, removeSubs)
                                }}
                                onCancel={() => setRemoveModalOpen(false)}
                            />

                            {/* Split Test Results Panel — opened via the Split node's
                                insights-icon action or by clicking a per-variant perf
                                card on canvas. onPromote opens the confirmation modal. */}
                            <SplitTestResultsPanel
                                journey={state.journey}
                                open={splitTestResultsPanelOpen}
                                onClose={() => setSplitTestResultsPanelOpen(false)}
                                onPromote={handlePromoteClick}
                                isDraft={splitTestIsDraft}
                                promotionDisabled={promotionDisabled}
                                // Block promote while the builder has unsaved changes: promote
                                // acts on the SAVED server graph, so promoting now would discard
                                // the edits and/or act on a stale variant set. Computed at render
                                // (detectChanges is pure) so it stays reactive; only when the
                                // panel is open to avoid the snapshot stringify cost otherwise.
                                promoteBlockedReason={
                                    splitTestResultsPanelOpen && !promotionDisabled && detectChanges()
                                        ? 'Save your changes before promoting'
                                        : undefined
                                }
                                activeSplit={activeSplit}
                                getContainer={appSvc.getAppContainer}
                            />

                            {/* Promote Variant Confirmation Modal */}
                            <PromoteVariantModal
                                open={promoteModalState.open}
                                variantName={promoteModalState.variantName}
                                otherVariantCount={promoteModalState.otherVariantCount}
                                onConfirm={handlePromoteConfirm}
                                onCancel={() =>
                                    setPromoteModalState((prev) => ({ ...prev, open: false }))
                                }
                            />
                            </SplitTestResultsContext.Provider>
                        </JourneyContext.Provider>
                    )
                )}
            </Well>
        )
    },
)
